<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-emerald-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-emerald-600 tracking-widest">Tesorería</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
          Cuentas Bancarias
          <span class="px-3 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 text-xs sm:text-sm font-black rounded-lg border border-emerald-200">
            {{ cuentas.length }}
          </span>
        </h1>
      </div>
      <div class="flex gap-2">
        <button @click="loadCuentas" class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 flex items-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" /> Actualizar
        </button>
        <button @click="openCreateModal" class="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" /> Nueva Cuenta
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div class="sm:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Buscar</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input v-model="searchQuery" type="text" placeholder="Titular, documento, cuenta, alias..." class="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Banco</label>
          <select v-model="filterBanco" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-sm dark:text-white outline-none">
            <option :value="null">TODOS</option>
            <option v-for="b in bancos" :key="b.idBanco" :value="b.idBanco">{{ b.nombre }}</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Moneda</label>
          <select v-model="filterMoneda" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-sm dark:text-white outline-none">
            <option value="all">TODAS</option>
            <option v-for="m in MONEDAS" :key="m.value" :value="m.value">{{ m.label }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Cards de cuentas -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      <div v-for="i in 6" :key="i" class="bg-white dark:bg-gray-800 rounded-2xl p-4 animate-pulse h-40"></div>
    </div>
    <div v-else-if="filteredCuentas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl p-16 text-center">
      <Icon icon="mdi:credit-card-off" class="text-6xl text-gray-300 mx-auto mb-3" />
      <p class="text-lg font-black text-gray-400">Sin cuentas registradas</p>
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      <div v-for="c in filteredCuentas" :key="c.idCuentaBancaria"
           class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all">
        <!-- Card header con gradiente por moneda -->
        <div :class="cardGradient(c.moneda)" class="px-4 py-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:bank" class="text-white text-2xl" />
            <div>
              <p class="text-white font-black text-sm">{{ c.bancoNombre }}</p>
              <p class="text-white/70 text-[10px] font-bold">{{ c.bancoCodigo }} · {{ c.moneda }}</p>
            </div>
          </div>
          <span :class="c.estado === 'activo' ? 'bg-white/30' : 'bg-black/30'" class="px-2 py-0.5 rounded-lg text-white text-[9px] font-black uppercase">{{ c.estado }}</span>
        </div>

        <!-- Card body -->
        <div class="p-4 space-y-3">
          <!-- Titular -->
          <div>
            <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Titular</p>
            <p class="text-sm font-black text-gray-900 dark:text-white">{{ c.titularNombreCompleto }}</p>
            <div class="flex flex-wrap items-center gap-2 mt-1">
              <span class="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-[10px] font-black">
                {{ formatearDocumentoTitular(c.titularTipoDocumento, c.titularDocumento) }}
              </span>
              <span v-if="c.esEmpresa" class="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-[10px] font-black">EMPRESA</span>
              <span v-if="!c.esCuentaPropia" class="px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-[10px] font-black">TERCERO</span>
            </div>
          </div>

          <!-- Teléfono y cuenta -->
          <div class="grid grid-cols-2 gap-2">
            <div>
              <p class="text-[9px] font-black text-gray-400 uppercase">Teléfono</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white">{{ c.titularTelefono || '—' }}</p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 uppercase">Tipo</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white">{{ formatearTipoCuenta(c.tipoCuenta) }}</p>
            </div>
          </div>

          <div>
            <p class="text-[9px] font-black text-gray-400 uppercase">N° Cuenta</p>
            <p class="text-xs font-black text-gray-900 dark:text-white font-mono tracking-tight">{{ formatearNumeroCuenta(c.numeroCuenta) }}</p>
          </div>

          <!-- Pago móvil si aplica -->
          <div v-if="c.pmTelefono || c.pmBancoCodigo" class="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
            <p class="text-[9px] font-black text-indigo-600 dark:text-indigo-300 uppercase mb-1 flex items-center gap-1">
              <Icon icon="mdi:cellphone" /> Pago Móvil
            </p>
            <div class="flex flex-wrap gap-2 text-[10px] font-bold text-indigo-800 dark:text-indigo-200">
              <span v-if="c.pmBancoCodigo">{{ c.pmBancoCodigo }}</span>
              <span v-if="c.pmTelefono">{{ c.pmTelefono }}</span>
              <span v-if="c.pmDocumento">{{ c.pmDocumento }}</span>
            </div>
          </div>

          <!-- Alias -->
          <p v-if="c.alias" class="text-[10px] text-gray-500 italic">{{ c.alias }}</p>
        </div>

        <!-- Acciones -->
        <div class="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex gap-1">
          <button @click="verResumen(c)" class="flex-1 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all text-[10px] font-black flex items-center justify-center gap-1">
            <Icon icon="mdi:chart-line" /> Resumen
          </button>
          <button @click="openEditModal(c)" class="flex-1 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all text-[10px] font-black flex items-center justify-center gap-1">
            <Icon icon="mdi:pencil" /> Editar
          </button>
          <button @click="abrirModalEliminar(c)" class="flex-1 py-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all text-[10px] font-black flex items-center justify-center gap-1">
            <Icon icon="mdi:delete" /> Eliminar
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL FORMULARIO CUENTA -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingCuenta ? 'mdi:pencil-box' : 'mdi:credit-card-plus'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ editingCuenta ? 'Editar Cuenta' : 'Nueva Cuenta Bancaria' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">Datos del titular y pago móvil</p>
            </div>
            <button @click="showModal = false" class="ml-auto p-1.5 bg-white/10 rounded-lg"><Icon icon="mdi:close" class="text-white" /></button>
          </div>

          <div class="p-6 overflow-y-auto max-h-[calc(92vh-160px)]">
            <form @submit.prevent="saveCuenta" class="space-y-4">
              <!-- Banco + Alias -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Banco *</label>
                    <select v-model="form.idBanco" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black dark:text-white outline-none">
                      <option :value="null" disabled>Selecciona banco</option>
                      <option v-for="b in bancos" :key="b.idBanco" :value="b.idBanco">{{ b.codigo }} — {{ b.nombre }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Alias</label>
                    <input v-model="form.alias" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm dark:text-white outline-none" placeholder="Ej: Banesco Principal" />
                  </div>
                </div>
              </div>

              <!-- Cuenta -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <p class="text-[10px] font-black text-gray-400 tracking-widest mb-3">DATOS DE LA CUENTA</p>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div class="sm:col-span-1">
                    <label class="block text-[10px] font-black text-gray-400 mb-2">N° Cuenta</label>
                    <input v-model="form.numeroCuenta" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-mono dark:text-white outline-none" placeholder="20 dígitos" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Tipo *</label>
                    <select v-model="form.tipoCuenta" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black dark:text-white outline-none">
                      <option v-for="t in TIPOS_CUENTA" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Moneda *</label>
                    <select v-model="form.moneda" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black dark:text-white outline-none">
                      <option v-for="m in MONEDAS" :key="m.value" :value="m.value">{{ m.label }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Titular -->
              <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <p class="text-[10px] font-black text-blue-600 dark:text-blue-300 tracking-widest mb-3 flex items-center gap-1">
                  <Icon icon="mdi:account" /> DATOS DEL TITULAR
                </p>
                <div class="space-y-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Nombre Completo *</label>
                    <input v-model="form.titularNombreCompleto" required maxlength="200" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold dark:text-white outline-none" placeholder="Ej: Jean Carlos Pérez Rodríguez" />
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 mb-2">Tipo Doc *</label>
                      <select v-model="form.titularTipoDocumento" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-black dark:text-white outline-none">
                        <option v-for="t in TIPOS_DOCUMENTO_TITULAR" :key="t.value" :value="t.value">{{ t.label }}</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 mb-2">Documento *</label>
                      <input v-model="form.titularDocumento" required maxlength="30" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-mono dark:text-white outline-none" placeholder="12345678" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 mb-2">Teléfono</label>
                      <input v-model="form.titularTelefono" maxlength="20" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm dark:text-white outline-none" placeholder="04141234567" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Email</label>
                    <input v-model="form.titularEmail" type="email" maxlength="100" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm dark:text-white outline-none" />
                  </div>
                  <div class="flex flex-wrap gap-4">
                    <label class="flex items-center gap-2 text-xs font-bold"><input type="checkbox" v-model="form.esEmpresa" class="w-4 h-4" /> Es empresa</label>
                    <label class="flex items-center gap-2 text-xs font-bold"><input type="checkbox" v-model="form.esCuentaPropia" class="w-4 h-4" /> Cuenta propia</label>
                  </div>
                </div>
              </div>

              <!-- Pago Móvil -->
              <div class="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
                <p class="text-[10px] font-black text-indigo-600 dark:text-indigo-300 tracking-widest mb-3 flex items-center gap-1">
                  <Icon icon="mdi:cellphone" /> DATOS PAGO MÓVIL
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Código Banco PM</label>
                    <input v-model="form.pmBancoCodigo" maxlength="4" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-sm font-mono dark:text-white outline-none" placeholder="0102" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Teléfono PM</label>
                    <input v-model="form.pmTelefono" maxlength="20" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-sm dark:text-white outline-none" placeholder="04141234567" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 mb-2">Documento PM</label>
                    <input v-model="form.pmDocumento" maxlength="30" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-sm font-mono dark:text-white outline-none" placeholder="12345678" />
                  </div>
                </div>
              </div>

              <!-- Otros canales -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Zelle Email</label>
                  <input v-model="form.zelleEmail" type="email" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Binance ID</label>
                  <input v-model="form.binanceId" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">PayPal Email</label>
                  <input v-model="form.paypalEmail" type="email" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm dark:text-white outline-none" />
                </div>
              </div>

              <!-- Estado -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-3 flex items-center gap-4">
                <span class="text-[10px] font-black text-gray-400">ESTADO:</span>
                <label class="flex items-center gap-2 text-xs font-bold"><input type="radio" v-model="form.estado" value="activo" /> Activo</label>
                <label class="flex items-center gap-2 text-xs font-bold"><input type="radio" v-model="form.estado" value="inactivo" /> Inactivo</label>
              </div>

              <div class="flex gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showModal = false" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 rounded-xl font-black text-xs">Cancelar</button>
                <button type="submit" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs uppercase shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" />
                  {{ saving ? 'Guardando...' : (editingCuenta ? 'Actualizar' : 'Crear Cuenta') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL RESUMEN -->
    <Teleport to="body">
      <div v-if="showResumenModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10001]" @click.self="showResumenModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center gap-3">
            <Icon icon="mdi:chart-line" class="text-white text-2xl" />
            <div>
              <h3 class="text-sm font-black text-white">Resumen de Cuenta</h3>
              <p class="text-[10px] text-white/70">{{ resumenCuenta?.bancoNombre }} · {{ resumenCuenta?.numeroCuenta }}</p>
            </div>
            <button @click="showResumenModal = false" class="ml-auto p-1.5 bg-white/10 rounded-lg"><Icon icon="mdi:close" class="text-white" /></button>
          </div>
          <div v-if="loadingResumen" class="p-10 text-center">
            <Icon icon="mdi:loading" class="animate-spin text-4xl text-blue-600" />
          </div>
          <div v-else-if="resumenCuenta" class="p-6 space-y-4">
            <div class="grid grid-cols-2 gap-3">
              <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
                <p class="text-[9px] font-black text-blue-500 uppercase">Total USD</p>
                <p class="text-2xl font-black text-blue-600">{{ formatearMonto(resumenCuenta.totalUsd, 'USD') }}</p>
              </div>
              <div class="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-center">
                <p class="text-[9px] font-black text-emerald-500 uppercase">Total Bs</p>
                <p class="text-2xl font-black text-emerald-600">{{ formatearMonto(resumenCuenta.totalBs, 'VES') }}</p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <div class="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-center">
                <p class="text-[9px] font-black text-gray-400 uppercase">Pagos</p>
                <p class="text-xl font-black text-gray-900 dark:text-white">{{ resumenCuenta.totalPagos }}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-center">
                <p class="text-[9px] font-black text-gray-400 uppercase">Primer Pago</p>
                <p class="text-xs font-black text-gray-900 dark:text-white">{{ formatFecha(resumenCuenta.primerPago) }}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-center">
                <p class="text-[9px] font-black text-gray-400 uppercase">Último Pago</p>
                <p class="text-xs font-black text-gray-900 dark:text-white">{{ formatFecha(resumenCuenta.ultimoPago) }}</p>
              </div>
            </div>
            <div v-if="Object.keys(resumenCuenta.totalPorMetodoUsd || {}).length">
              <p class="text-[10px] font-black text-gray-400 tracking-widest mb-2">POR MÉTODO (USD)</p>
              <div class="space-y-1">
                <div v-for="(monto, metodo) in resumenCuenta.totalPorMetodoUsd" :key="metodo" class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <span class="text-xs font-bold flex items-center gap-2">
                    <Icon :icon="getMetodoPagoIcon(metodo)" class="text-gray-500" />
                    {{ formatearMetodoPago(metodo) }}
                  </span>
                  <span class="text-sm font-black text-blue-600">{{ formatearMonto(monto, 'USD') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN ELIMINAR -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10002]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="px-6 py-4 bg-gradient-to-r from-red-600 to-pink-600 flex items-center gap-3">
            <Icon icon="mdi:alert-circle" class="text-white text-2xl" />
            <div>
              <h3 class="text-sm font-black text-white">Eliminar Cuenta</h3>
              <p class="text-[10px] text-white/80">No se puede deshacer</p>
            </div>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">¿Eliminar la cuenta <strong>{{ confirmModalCuenta?.bancoNombre }}</strong> de <strong>{{ confirmModalCuenta?.titularNombreCompleto }}</strong>?</p>
          </div>
          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-black text-xs">Cancelar</button>
            <button @click="ejecutarEliminar" :disabled="confirmModalCargando" class="flex-[2] py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase flex items-center justify-center gap-2">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" />
              {{ confirmModalCargando ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import {
  obtenerBancos, obtenerCuentasBancarias, crearCuentaBancaria,
  actualizarCuentaBancaria, eliminarCuentaBancaria, obtenerResumenCuenta,
  TIPOS_CUENTA, MONEDAS, TIPOS_DOCUMENTO_TITULAR,
  formatearTipoCuenta, formatearDocumentoTitular, formatearNumeroCuenta,
  formatearMonto, formatearMetodoPago, getMetodoPagoIcon
} from '@/api/bancos.js';

export default {
  name: 'CuentasBancariasList',
  components: { Icon },
  data() {
    return {
      TIPOS_CUENTA, MONEDAS, TIPOS_DOCUMENTO_TITULAR,
      cuentas: [], bancos: [],
      loading: false, saving: false,
      showModal: false,
      showResumenModal: false,
      loadingResumen: false,
      resumenCuenta: null,
      searchQuery: '',
      filterBanco: null,
      filterMoneda: 'all',
      editingCuenta: null,
      form: this.formVacio(),
      showConfirmModal: false,
      confirmModalCuenta: null,
      confirmModalCargando: false
    };
  },
  computed: {
    filteredCuentas() {
      let f = [...this.cuentas];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        f = f.filter(c =>
          c.titularNombreCompleto?.toLowerCase().includes(q) ||
          c.titularDocumento?.toLowerCase().includes(q) ||
          c.numeroCuenta?.toLowerCase().includes(q) ||
          c.alias?.toLowerCase().includes(q) ||
          c.bancoNombre?.toLowerCase().includes(q)
        );
      }
      if (this.filterBanco) f = f.filter(c => c.idBanco === this.filterBanco);
      if (this.filterMoneda !== 'all') f = f.filter(c => c.moneda === this.filterMoneda);
      return f;
    }
  },
  mounted() {
    this.loadBancos();
    this.loadCuentas();
  },
  methods: {
    formVacio() {
      return {
        idCuentaBancaria: null, idBanco: null, alias: '', numeroCuenta: '',
        tipoCuenta: 'corriente', moneda: 'VES',
        titularNombreCompleto: '', titularTipoDocumento: 'cedula',
        titularDocumento: '', titularTelefono: '', titularEmail: '',
        esEmpresa: false, esCuentaPropia: true,
        pmBancoCodigo: '', pmTelefono: '', pmDocumento: '',
        zelleEmail: '', binanceId: '', paypalEmail: '',
        estado: 'activo'
      };
    },
    cardGradient(moneda) {
      return {
        VES: 'bg-gradient-to-r from-emerald-600 to-teal-600',
        USD: 'bg-gradient-to-r from-blue-600 to-indigo-600',
        USDT: 'bg-gradient-to-r from-amber-500 to-orange-600',
        EUR: 'bg-gradient-to-r from-purple-600 to-pink-600'
      }[moneda] || 'bg-gradient-to-r from-gray-600 to-gray-800';
    },
    formatearTipoCuenta, formatearDocumentoTitular, formatearNumeroCuenta,
    formatearMonto, formatearMetodoPago, getMetodoPagoIcon,
    formatFecha(f) {
      if (!f) return '—';
      try { return new Date(f).toLocaleDateString('es-VE'); } catch { return f; }
    },
    async loadBancos() {
      const res = await obtenerBancos();
      if (res.success) this.bancos = res.data;
    },
    async loadCuentas() {
      this.loading = true;
      try {
        const res = await obtenerCuentasBancarias();
        if (res.success) this.cuentas = res.data;
      } finally { this.loading = false; }
    },
    openCreateModal() {
      this.editingCuenta = null;
      this.form = this.formVacio();
      this.showModal = true;
    },
    openEditModal(c) {
      this.editingCuenta = c;
      this.form = { ...c };
      this.showModal = true;
    },
    async saveCuenta() {
      this.saving = true;
      try {
        const res = this.editingCuenta
          ? await actualizarCuentaBancaria(this.editingCuenta.idCuentaBancaria, this.form)
          : await crearCuentaBancaria(this.form);
        if (res.success) {
          await this.loadCuentas();
          this.showModal = false;
        }
      } finally { this.saving = false; }
    },
    abrirModalEliminar(c) {
      this.confirmModalCuenta = c;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCuenta = null;
    },
    async ejecutarEliminar() {
      this.confirmModalCargando = true;
      try {
        const res = await eliminarCuentaBancaria(this.confirmModalCuenta.idCuentaBancaria);
        if (res.success) {
          await this.loadCuentas();
          this.cancelarConfirmacion();
        }
      } finally { this.confirmModalCargando = false; }
    },
    async verResumen(c) {
      this.showResumenModal = true;
      this.loadingResumen = true;
      this.resumenCuenta = null;
      try {
        const res = await obtenerResumenCuenta(c.idCuentaBancaria);
        if (res.success) this.resumenCuenta = res.data;
      } finally { this.loadingResumen = false; }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity:0; transform:scale(.95);} to {opacity:1; transform:scale(1);} }
.animate-zoomIn { animation: zoomIn .2s ease-out; }
</style>